import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Eye, Calendar, User, ExternalLink } from 'lucide-react';

const articleTypeLabels = {
  research: 'Recherche',
  case_study: 'Étude de cas',
  review: 'Revue',
  practice_note: 'Note clinique',
  protocol: 'Protocole'
};

const articleTypeColors = {
  research: 'bg-blue-100 text-blue-700',
  case_study: 'bg-purple-100 text-purple-700',
  review: 'bg-green-100 text-green-700',
  practice_note: 'bg-amber-100 text-amber-700',
  protocol: 'bg-cyan-100 text-cyan-700'
};

export const ArticleCard = ({ article, variant = 'default' }) => {
  const formattedDate = new Date(article.published_at || article.created_at).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  const typeColor = articleTypeColors[article.article_type] || 'bg-gray-100 text-gray-700';

  // Compact variant for sidebar or lists
  if (variant === 'compact') {
    return (
      <Link to={`/articles/${article.article_id}`} className="block group">
        <div className="py-3 border-b last:border-0 hover:bg-muted/50 px-2 -mx-2 rounded transition-colors">
          <Badge className={`${typeColor} border-0 text-xs mb-1`}>
            {articleTypeLabels[article.article_type] || article.article_type}
          </Badge>
          <h4 className="font-medium text-sm leading-tight group-hover:text-primary transition-colors line-clamp-2">
            {article.title}
          </h4>
          <p className="text-xs text-muted-foreground mt-1">
            {article.author_name} • {formattedDate}
          </p>
        </div>
      </Link>
    );
  }

  // Full card variant (default)
  return (
    <Link to={`/articles/${article.article_id}`} data-testid={`article-card-${article.article_id}`}>
      <Card className="h-full hover:shadow-lg transition-all duration-300 cursor-pointer group border-l-4 border-l-primary/20 hover:border-l-primary">
        <CardContent className="p-5">
          {/* Header with type and volume */}
          <div className="flex items-center justify-between mb-3">
            <Badge className={`${typeColor} border-0 text-xs`}>
              {articleTypeLabels[article.article_type] || article.article_type}
            </Badge>
            {article.volume && article.issue && (
              <span className="text-xs text-muted-foreground">
                Vol. {article.volume}, N° {article.issue}
              </span>
            )}
          </div>

          {/* Title */}
          <h3 className="font-serif text-base font-semibold leading-snug group-hover:text-primary transition-colors mb-3 line-clamp-2">
            {article.title}
          </h3>

          {/* Author info */}
          <div className="flex items-center gap-2 mb-3 text-sm">
            <User className="w-4 h-4 text-muted-foreground" />
            <span className="text-muted-foreground">{article.author_name}</span>
            {article.author_orcid && (
              <a
                href={`https://orcid.org/${article.author_orcid}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex"
                title="ORCID"
              >
                <img src="https://orcid.org/assets/vectors/orcid.logo.icon.svg" alt="ORCID" className="w-4 h-4" />
              </a>
            )}
          </div>

          {/* Abstract */}
          <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
            {article.abstract}
          </p>

          {/* Keywords */}
          {article.keywords && article.keywords.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-4">
              {article.keywords.slice(0, 4).map((keyword, idx) => (
                <span key={idx} className="text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground">
                  {keyword}
                </span>
              ))}
              {article.keywords.length > 4 && (
                <span className="text-xs text-muted-foreground">
                  +{article.keywords.length - 4}
                </span>
              )}
            </div>
          )}

          {/* Footer with metrics */}
          <div className="flex items-center justify-between pt-3 border-t text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>{formattedDate}</span>
              </div>
              {article.views > 0 && (
                <div className="flex items-center gap-1">
                  <Eye className="w-3.5 h-3.5" />
                  <span>{article.views} vues</span>
                </div>
              )}
            </div>
            {article.doi && (
              <div className="flex items-center gap-1 text-primary">
                <ExternalLink className="w-3 h-3" />
                <span className="font-mono text-xs">DOI</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
